import '../CSS File/CarouselComponent.css';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import { Link, useNavigate } from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';
import { getToken } from '../utils/cookieUtils';
import Spinner from "../element/BePatient";

import { RiArrowRightDoubleLine } from "react-icons/ri";
import SubscriptionCard from './SubscriptionCard';
import SubscriptionConsentDialog from './SubscriptionConsentDialog';
import toast from 'react-hot-toast';
import { useLocation } from "react-router-dom";
import { apiCall, getJsonResponse } from '../utils/apiUtils';

const SubscriptionPlans = ({ isOpen, onClose }) => {  // Fixed props destructuring
  const [activeIndex, setActiveIndex] = useState(0);
  const [pricingOptions, setPricingOptions] = useState([]);
  const [currentPlan, setCurrentPlan] = useState({});
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // Popup states
  const [showWeeklyConsentPopup, setShowWeeklyConsentPopup] = useState(false);
  const [showMultipleTimesConsentPopup, setShowMultipleTimesConsentPopup] = useState(false);
  const [pendingSubscription, setPendingSubscription] = useState(null); // {id, isDefault, planName}

  const userId = localStorage.getItem("logged_user_id");
  const location = useLocation();
  const { postalCode } = location.state || {};

  // Memoized fetch functions
  const fetchSubscriptionData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiCall(
        `${process.env.REACT_APP_BASE_URL}subscription/fetch_subscription_plan`,
        {
          method: 'POST',
          body: JSON.stringify({
            postal_code: postalCode || localStorage.getItem('postalCode'),
          }),
        }
      );
      const responseData = await getJsonResponse(response);
      setPricingOptions(responseData.data || []);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
    } finally {
      setLoading(false);
    }
  }, [postalCode]);

  const checkSubscription = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}subscription/check_current_plan`,
        {
          method: 'POST',
          headers: {
            'Authorization': 'Bearer ' + getToken(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: localStorage.getItem('logged_user_id'),
            postal_code: postalCode || localStorage.getItem("postalCode")
          }),
        }
      );
      const res_json = await response.json();
      if (res_json.status) {
        setCurrentPlan(res_json);
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  }, [postalCode]);

  useEffect(() => {
    const fetchData = async () => {
      await fetchSubscriptionData();
      if (userId) await checkSubscription();
    };
    fetchData();
  }, [fetchSubscriptionData, checkSubscription, userId]);

  useEffect(() => {
    if (pricingOptions.length > 0) {
      console.log("Pricing Options:", pricingOptions);
      console.log("Current Plan:", currentPlan);
      
      // Find the current plan index
      const currentPlanIndex = pricingOptions.findIndex(option => {
        console.log("Checking:", option);
        return option.subscription_plan_id === currentPlan.plan_id && currentPlan.plan_status;
      });
      
      console.log("Current Plan Index:", currentPlanIndex);
      
      // If not found, use the default plan index
      const finalIndex = currentPlanIndex !== -1 
        ? currentPlanIndex 
        : pricingOptions.findIndex(option => option.default_plan === "1");
      
      console.log("Final Selected Plan Index:", finalIndex);
      console.log("Final Selected Plan:", pricingOptions[finalIndex] || "No Plan Found");
      
      setActiveIndex(finalIndex >= 0 ? finalIndex : 0);
    }
  }, [currentPlan, pricingOptions]);

  // Helper function to check if plan is Weekly subscription
  const isWeeklyPlan = (planName) => {
    if (!planName) return false;
    const name = planName.toLowerCase();
    return name.includes('weekly') && !name.includes('multiple');
  };

  // Helper function to check if plan is Multiple Times subscription
  const isMultipleTimesPlan = (planName) => {
    if (!planName) return false;
    const name = planName.toLowerCase();
    return name.includes('multiple');
  };

  const handlePlanSelection = async (id, isDefault, planName = '') => {
    // Check if it's a default plan (no subscription)
    const isDefaultPlan = isDefault === "1" || isDefault === 1 || isDefault === true;
    
    // For subscription plans, show consent popup first
    if (!isDefaultPlan && userId) {
      // Find the plan name if not provided
      if (!planName) {
        const plan = pricingOptions.find(opt => opt.subscription_plan_id === id);
        planName = plan?.plan_name || '';
      }
      
      // Check plan type and show appropriate popup
      if (isWeeklyPlan(planName)) {
        setPendingSubscription({ id, isDefault, planName });
        setShowWeeklyConsentPopup(true);
        return;
      } else if (isMultipleTimesPlan(planName)) {
        setPendingSubscription({ id, isDefault, planName });
        setShowMultipleTimesConsentPopup(true);
        return;
      }
    }
    
    // Proceed with subscription (for default plans or after consent)
    await proceedWithSubscription(id, isDefault);
  };

  const proceedWithSubscription = async (id, isDefault) => {
    const isDefaultPlan = isDefault === "1" || isDefault === 1 || isDefault === true;
    
    if (!userId) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}subscription/fetch_plan_details`,
          {
            method: 'POST',
            headers: {
              'Authorization': 'Bearer ' + getToken(),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ plan_id: id }),
          }
        );
  
        const responseData = await response.json();
  
        if (responseData.status) {
          localStorage.setItem("chef_status", responseData.chef_status);
          localStorage.setItem("status", responseData.status);
          localStorage.setItem("plan_status", responseData.plan_status);
          localStorage.setItem("plan_id", responseData.plan_id);
          localStorage.setItem("plan_name", responseData.plan_name);
          localStorage.setItem("price", responseData.price);
          
          // Default plan goes directly to chef-list, subscription plans go to address details
          if (isDefaultPlan) {
            navigate('/chef-list');
          } else {
            navigate('/add-Address-details');
          }
        } else {
          toast.error('Please login before subscribing, then apply Subscription Plan');
        }
  
        return;
      } catch (error) {
        console.error('Error fetching subscription data:', error);
        toast.error('Error fetching subscription data');
        return;
      }
    }
  
    // If user is logged in, proceed with adding the subscription plan
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}subscription/add_subscription_plan`,
        {
          method: 'POST',
          headers: {
            'Authorization': 'Bearer ' + getToken(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ plan_id: id, user_id: userId }),
        }
      );
  
      const responseData = await response.json();
  
      if (responseData.status) {
        localStorage.setItem("plan_status", isDefaultPlan ? "false" : "true");
        
        // Default plan goes directly to chef-list, subscription plans go to address details
        if (isDefaultPlan) {
          navigate('/chef-list');
        } else {
          navigate('/add-Address-details');
        }
      } else {
        toast.error(responseData.message || 'Subscription failed');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Error processing subscription');
    }
  };

  // Consent texts
  const weeklyConsentText = `By subscribing, I agree to a minimum commitment period of two (2) consecutive weeks of completed bookings. Early cancellation may result in fees (see Terms and Conditions).  I understand that missed or incomplete bookings do not pause, waive, or reduce this commitment.`;

  const multipleTimesConsentText = `By subscribing, I agree to a minimum commitment period of two (2) consecutive weeks of completed bookings. Early cancellation may result in fees (see Terms and Conditions). I understand that missed or incomplete bookings do not pause, waive, or reduce this commitment.`;

  const settings = {
    centerMode: true,
    centerPadding: '0px',
    slidesToShow: Math.min(3, pricingOptions.length),
    focusOnSelect: true,
    beforeChange: (current, next) => setActiveIndex(next),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          centerMode: false
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          centerMode: false
        }
      }
    ]
  };

  return (
    <>
      <div className="container">
        {loading ? (
          <div className="loader">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <div className="App">
            <Link
              to={
                localStorage.getItem("logged_user_id") ? "/chef-list" : "/sign-up"
              }
              style={{ textDecoration: "none" }}
            >
              <div className="skip mt-0">
                skip
                <span>
                  <RiArrowRightDoubleLine />
                </span>
              </div>
            </Link>

            <p style={{ color: "red", padding: '15px', whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
              Pricing is as per the Service Address Zip Code
            </p>
            <div className="pricing-container">
              {pricingOptions.map((option, index) => (
                <SubscriptionCard
                  key={option.plan_id}
                  activeIndex={activeIndex}
                  index={index}
                  type={option.plan_name}
                  price={option.price}
                  features={option.description}
                  buttonText={option.button_text}
                  option={option}
                  onSubscribe={(opt) =>
                    handlePlanSelection(
                      opt.subscription_plan_id,
                      opt.default_plan,
                      opt.plan_name
                    )
                  }
                  changeCard={() => setActiveIndex(index)}
                />
              ))}
            </div>
            <div
              className="cancel-subscription-plan text-center mt-5"
              style={{
                color: "#FF5E41",
                display: currentPlan.plan_status ? "block" : "none",
                cursor: 'pointer',
              }}
              onClick={() => {
                navigate('/subscription-summary');
              }}
            >
              Cancel Subscription Anytime
            </div>
          </div>
        )}

        <div className="mobile">
          <Link
            to={
              localStorage.getItem("logged_user_id") ? "/chef-list" : "/sign-up"
            }
            style={{ textDecoration: "none" }}
          >
            <div className="skip mt-0">
              skip
              <span>
                <RiArrowRightDoubleLine />
              </span>
            </div>
          </Link>

          <p style={{ color: "red", padding: '15px', whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
            Pricing is as per the Service Address Zip Code
          </p>

          {pricingOptions.map((option, index) => (
            <div className="div my-3" key={option.subscription_plan_id}>
              <div
                className={`mobile-card ${
                  index === activeIndex ? "active" : ""
                }`}
                onClick={() => setActiveIndex(index)}
              >
                <div className="card-header-mobile text-center">
                  <h3>{option.plan_name}</h3>
                  <div className="price">
                    ${option.price}<span>/booking</span>
                  </div>
                </div>

                <ul
                  className="card-features"
                  style={{ listStyleType: "disc", paddingLeft: "20px" }}
                >
                  {option.description.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
                <div className="row text-center">
                  <button
                    className="card-button-mobile"
                    onClick={() =>
                      handlePlanSelection(option.subscription_plan_id, option.default_plan, option.plan_name)
                    }
                  >
                    {option.button_text}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Weekly Subscription Consent Popup */}
      <SubscriptionConsentDialog
        isOpen={showWeeklyConsentPopup}
        onClose={() => {
          setShowWeeklyConsentPopup(false);
          setPendingSubscription(null);
        }}
        consentText={weeklyConsentText}
        onAgree={async () => {
          setShowWeeklyConsentPopup(false);
          if (pendingSubscription) {
            await proceedWithSubscription(pendingSubscription.id, pendingSubscription.isDefault);
            setPendingSubscription(null);
          }
        }}
        buttonText="Agree"
      />

      {/* Multiple Times Subscription Consent Popup */}
      <SubscriptionConsentDialog
        isOpen={showMultipleTimesConsentPopup}
        onClose={() => {
          setShowMultipleTimesConsentPopup(false);
          setPendingSubscription(null);
        }}
        consentText={multipleTimesConsentText}
        onAgree={async () => {
          setShowMultipleTimesConsentPopup(false);
          if (pendingSubscription) {
            await proceedWithSubscription(pendingSubscription.id, pendingSubscription.isDefault);
            setPendingSubscription(null);
          }
        }}
        buttonText="Agree"
      />
    </>
  );
};

export default SubscriptionPlans;